/**
 * AgentEvaluationsPage — the EVALUATE/IMPROVE surface of MASTER_FLOW §15A
 * (FULL-0128 / PROD-OPS-042), at /agent-evaluations.
 *
 * The page is the first product consumer of the four read-only evaluation evidence routes that
 * FULL-0111, FULL-0123 and FULL-0126 shipped behind an Admin/Owner gate:
 *
 *     GET /api/agents/product-eval/contract
 *     GET /api/agents/product-eval/promotion/contract
 *     GET /api/agents/product-eval/promotion/evidence
 *     GET /api/agents/product-eval/promotion/promotions
 *
 * It mounts none of `record`, `approvals` or `promote`. Those stay the explicit operator path: a
 * candidate is graded through the server's review seam, an ADOPT record needs a single-use approval
 * bound to its exact evidence digest, and a SECOND person consumes it. This screen neither submits
 * nor approves and has no control of its own.
 *
 * Failure states are separate sentences. A refusal (403) is a permission answer for the page; one
 * failed source is a failed source and the other sections still render; an answered-but-empty store
 * is a real zero and says so. `SourceGate` in the panel owns the loading/failed/not-reported arms.
 */
import { useQuery } from "@tanstack/react-query";

import AgentEvaluationWorkspacePanel, {
  SOURCE_FAILED,
  SOURCE_LOADING,
  SOURCE_READY,
} from "../components/AgentEvaluationWorkspacePanel";
import { productEvalApi } from "../services/api";

const EVIDENCE_LIMIT = 50;

function sourceState(query) {
  if (query.isLoading) return SOURCE_LOADING;
  if (query.isError) return SOURCE_FAILED;
  return SOURCE_READY;
}

function isRefused(query) {
  return query.error?.response?.status === 403;
}

export default function AgentEvaluationsPage() {
  const contractQuery = useQuery({
    queryKey: ["product-eval-contract"],
    queryFn: () => productEvalApi.contract(),
    staleTime: 60_000,
  });

  const promotionContractQuery = useQuery({
    queryKey: ["product-eval-promotion-contract"],
    queryFn: () => productEvalApi.promotionContract(),
    staleTime: 60_000,
  });

  const evidenceQuery = useQuery({
    queryKey: ["product-eval-evidence", EVIDENCE_LIMIT],
    queryFn: () => productEvalApi.evidence({ limit: EVIDENCE_LIMIT }),
  });

  const promotionsQuery = useQuery({
    queryKey: ["product-eval-promotions", EVIDENCE_LIMIT],
    queryFn: () => productEvalApi.promotions({ limit: EVIDENCE_LIMIT }),
  });

  const refused = [contractQuery, promotionContractQuery, evidenceQuery, promotionsQuery].some(
    isRefused,
  );

  if (refused) {
    return (
      <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
        <Header />
        <p
          role="alert"
          data-testid="evaluations-refused"
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
        >
          אין הרשאה לצפות בראיות ההערכה. המסלול זמין לתפקידי מנהל ובעלים בלבד.
        </p>
      </div>
    );
  }

  const states = {
    contract: sourceState(contractQuery),
    promotionContract: sourceState(promotionContractQuery),
    evidence: sourceState(evidenceQuery),
    promotions: sourceState(promotionsQuery),
  };

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <Header />

      <p
        data-testid="evaluations-purpose"
        className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2"
      >
        סביבת הערכה לקריאה בלבד. המסך מציג את מערכי ההערכה, הראיות שנשמרו והחלטות הקידום האנושיות
        שהשרת מדווח עליהן. הוא אינו מגיש מועמד, אינו מקדם, אינו משנה ברירת מחדל, ניתוב או הרשאה.
        הדרך היחידה לשנות מצב היא מסלול ההקלטה והאישור הנפרד, בשני אנשים.
      </p>

      <AgentEvaluationWorkspacePanel
        contract={contractQuery.data}
        promotionContract={promotionContractQuery.data}
        evidence={evidenceQuery.data}
        promotions={promotionsQuery.data}
        states={states}
      />
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
        <span className="text-gray-600">סביבת הערכה</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800">🧪 סביבת הערכה</h1>
      <p className="text-sm text-gray-500 mt-0.5">
        ראיות הערכה, שערים והחלטות קידום של עובדים דיגיטליים — לקריאה בלבד
      </p>
    </div>
  );
}
